import { createSlice } from '@reduxjs/toolkit'
import LoginService from '../../services/LoginService';
import { LocalStorageManager } from '../../utils/LocalStorageManager';
import { toast } from './notificationReducer';
import { authConstants } from 'constants/authConstants';
import { format, isBefore, isSameDay } from 'date-fns';
import { CommonTexts, centersTexts } from 'locale/es';
import { selectCenter, setSelectedCenter } from './selectedCenterReducer';
import CentersService from 'services/CentersService'
import { updateCenter } from './centersReducer';
import { RolesManager } from 'utils/RolesManager';

const { setLocalStorageItem, removeLocalStorageItem } = LocalStorageManager

const parseMonitoringRoles = (roles) => Array.isArray(roles) ? roles.map(role => role.name) : [roles.name];

const initialState = {
  user: null,
  isAuthenticated: false,
  error: null,
  loading: false,
}

const loggedUserSlice = createSlice({
  name: 'userAuthentication',
  initialState: initialState,
  reducers: {
    login(state, action) {
      const { user } = action.payload

      return {
        ...state,
        user,
        isAuthenticated: true,
        error: null
      }
    },
    logout(state, action) {
      removeLocalStorageItem(authConstants.MonitoringUserNew)

      return initialState
    },
    appendUserCenter(state, action) {
      const oldLocalStorageState = LocalStorageManager.getLocalStorageItem(authConstants.MonitoringUserNew)

      const newLocalStorageState = { ...oldLocalStorageState, centers: [...oldLocalStorageState.centers, action.payload] }

      LocalStorageManager.setLocalStorageItem(authConstants.MonitoringUserNew, newLocalStorageState)

      return { ...state, user: { ...state.user, centers: [...state.user.centers, action.payload] } }
    },
    updateUserCenter(state, action) {
      const { data } = action.payload

      const updatedCenters = state.user.centers.map((center) =>
        center.id !== data.id ? center : data
      )

      const oldLocalStorageState = LocalStorageManager.getLocalStorageItem(authConstants.MonitoringUserNew)

      const newLocalStorageState = { ...oldLocalStorageState, centers: updatedCenters }

      LocalStorageManager.setLocalStorageItem(authConstants.MonitoringUserNew, newLocalStorageState)

      return { ...state, user: { ...state.user, centers: updatedCenters }, loading: false }
    },
    removeUserCenter(state, action) {
      const updatedCenters = state.user.centers.filter((center) => center.id !== action.payload)

      const oldLocalStorageState = LocalStorageManager.getLocalStorageItem(authConstants.MonitoringUserNew)

      const newLocalStorageState = { ...oldLocalStorageState, centers: updatedCenters }

      LocalStorageManager.setLocalStorageItem(authConstants.MonitoringUserNew, newLocalStorageState)

      return { ...state, user: { ...state.user, centers: updatedCenters }, loading: false }
    },
    setCentersLoading(state, action) {
      state.loading = true
      state.error = null;
    },
    setCentersError(state, action) {
      state.loading = false
      state.error = action.payload;
    }
  },
})

export const { login, logout, appendUserCenter, updateUserCenter, setCentersLoading, setCentersError, removeUserCenter } = loggedUserSlice.actions

export const loginUser = (credentials) => {
  return async (dispatch) => {
    try {
      const { data: monitoringLogin } = await LoginService.login(credentials)

      const { data: studiesLogin } = await LoginService.loginStudies(credentials);

      const {
        token: monitoringToken,
        expires_at: monitoringTokenExpirationDate,
        professional: monitoringProfessional,
      } = monitoringLogin

      const {
        access_token_user: studiesToken,
        expires_at: studiesTokenExpirationDate
      } = studiesLogin.data

      const formattedUser = {
        details: {
          id: monitoringProfessional.id,
          firstName: monitoringProfessional.user.firstName,
          lastName: monitoringProfessional.user.lastName,
          fullName: `${monitoringProfessional.user.firstName} ${monitoringProfessional.user.lastName}`,
          login: monitoringProfessional.user.login,
          email: monitoringProfessional.user.email,
        },
        tokens: {
          monitoringToken,
          monitoringTokenExpirationDate,
          studiesToken,
          studiesTokenExpirationDate
        },
        roles: parseMonitoringRoles(monitoringProfessional.user.authorities),
        centers: monitoringProfessional.centers
      }

      setLocalStorageItem(authConstants.MonitoringUserNew, formattedUser)

      dispatch(login({ user: formattedUser }))

      dispatch(setSelectedCenter(formattedUser.centers[0]))
    } catch (error) {
      console.error(error.message);
      throw error
    }
  }
}

export const keepUserSessionAlive = (user) => {
  return (dispatch) => {
    if (!user) {
      dispatch(logout())
    }

    const { tokens } = user

    if (!tokens) {
      dispatch(logout())
    }

    const { monitoringTokenExpirationDate, studiesTokenExpirationDate } = tokens

    if (!monitoringTokenExpirationDate || !studiesTokenExpirationDate) {
      dispatch(logout())
    }

    // * We remove hours, minutes and seconds so we only deal with dd/MM/yyyy
    const formattedToday = new Date(format(new Date(), 'yyyy-MM-dd'))
    const formattedMonitoringTokenExpirationDate = new Date(format(new Date(monitoringTokenExpirationDate), 'yyyy-MM-dd'))
    const formattedStudiesTokenExpirationDate = new Date(format(new Date(studiesTokenExpirationDate), 'yyyy-MM-dd'))

    // * If token is about to expire we logout so user can login again to refresh tokens
    const isMonitoringTokenAboutToExpire = isBefore(formattedMonitoringTokenExpirationDate, formattedToday) || isSameDay(formattedMonitoringTokenExpirationDate, formattedToday)
    const isStudiesTokenAboutToExpire = isBefore(formattedStudiesTokenExpirationDate, formattedToday) || isSameDay(formattedStudiesTokenExpirationDate, formattedToday)

    if (isMonitoringTokenAboutToExpire || isStudiesTokenAboutToExpire) {
      dispatch(toast(CommonTexts.EXPIRED_SESSION, "error"))

      return setTimeout(() => {
        dispatch(logout())
      }, 3000);
    }

    dispatch(login({ user }))

    dispatch(setSelectedCenter(user.centers[0]))
  }
}

export const updateUserCenterAction = (centerObj) => {
  return async (dispatch, getState) => {
    try {
      const { data } = await CentersService.update(centerObj.id, centerObj)
      dispatch(updateUserCenter({ data }))
      dispatch(selectCenter(data))
      dispatch(toast(centersTexts.EDITED, 'success'))

      const userRoles = getState().userAuthentication?.user?.roles

      if (!userRoles) return

      const userIsSuperAdmin = RolesManager.userIsSuperAdmin(userRoles)

      if (userIsSuperAdmin) {
        dispatch(updateCenter({ data }))
      }

      return data;
    } catch (error) {
      console.error(error)
      dispatch(setCentersError({ message: error.message, status: error.response?.status }));
      dispatch(toast(centersTexts.EDITED_ERROR, 'error'))
    }
  }
}

export default loggedUserSlice.reducer